(() => {
  if (window.mervauxRecentlyViewed) return;
  const key = 'mervaux:recently-viewed:v1';
  const maximum = 12;
  const lifetime = 30 * 24 * 60 * 60 * 1000;
  const views = new Set();
  let recordedHandle = '';
  let loadingPrivacy = false;
  const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true; }
    catch { return false; }
  };
  const removeHistory = () => {
    try { window.localStorage.removeItem(key); } catch {}
  };
  const history = () => {
    if (!allowed()) return [];
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw || raw.length > 8192) return [];
      const stored = JSON.parse(raw);
      if (!Array.isArray(stored)) return [];
      const now = Date.now();
      const seen = new Set();
      return stored.filter((item) => {
        if (!item || !validHandle(item.handle) || !Number.isFinite(item.seenAt) || item.seenAt > now + 60000 || now - item.seenAt > lifetime || seen.has(item.handle)) return false;
        seen.add(item.handle);
        return true;
      }).slice(0, maximum);
    } catch { return []; }
  };
  const renderViews = () => {
    const items = history();
    views.forEach((view) => view.load(items));
  };
  const refresh = () => {
    if (!allowed()) {
      removeHistory();
      recordedHandle = '';
      views.forEach((view) => view.clear());
      return;
    }
    const handle = document.querySelector('[data-mervaux-recently-viewed-config]')?.dataset.productHandle;
    if (!window.Shopify?.designMode && validHandle(handle) && handle !== recordedHandle) {
      const items = [{ handle, seenAt: Date.now() }, ...history().filter((item) => item.handle !== handle)].slice(0, maximum);
      try { window.localStorage.setItem(key, JSON.stringify(items)); recordedHandle = handle; } catch {}
    }
    renderViews();
  };
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed) { refresh(); return; }
    if (loadingPrivacy || !window.Shopify?.loadFeatures) return;
    loadingPrivacy = true;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      loadingPrivacy = false;
      if (!error) refresh();
    });
  };
  const safeRoot = (value) => {
    try {
      const url = new URL(value || '/', document.baseURI);
      if (url.origin !== new URL(document.baseURI).origin || !/^https?:$/.test(url.protocol)) return null;
      url.search = '';
      url.hash = '';
      if (!url.pathname.endsWith('/')) url.pathname += '/';
      return url;
    } catch { return null; }
  };
  const safeImage = (value) => {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value, document.baseURI);
      const origin = new URL(document.baseURI).origin;
      if (url.protocol !== 'https:' || (url.origin !== origin && url.hostname !== 'cdn.shopify.com')) return null;
      url.searchParams.set('width', '420');
      return url.href;
    } catch { return null; }
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.querySelector('[data-clear-history]')?.addEventListener('click', () => {
        removeHistory();
        views.forEach((view) => view.clear());
      }, { signal: this.events.signal });
      views.add(this);
      this.load(history());
      loadPrivacy();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      views.delete(this);
    }
    clear() {
      this.request?.abort();
      this.hidden = true;
      this.querySelector('[data-recent-list]')?.replaceChildren();
    }
    async load(items) {
      this.clear();
      if (!allowed()) return;
      const selected = items.filter((item) => item.handle !== this.dataset.currentHandle).slice(0, 6);
      const root = safeRoot(this.dataset.root);
      const list = this.querySelector('[data-recent-list]');
      if (!selected.length || !root || !list) return;
      const request = new AbortController();
      this.request = request;
      const options = { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } };
      try {
        const cartResponse = await fetch(new URL('cart.js', root), options);
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        const currency = typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency) ? cart.currency : this.dataset.currency;
        const formatter = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency });
        const results = await Promise.all(selected.map(async ({ handle }) => {
          try {
            const response = await fetch(new URL(`products/${encodeURIComponent(handle)}.js`, root), options);
            if (!response.ok) return null;
            const product = await response.json();
            if (product.handle !== handle || typeof product.title !== 'string' || !Number.isFinite(product.price) || product.price < 0) return null;
            const image = safeImage(product.featured_image);
            if (!image) return null;
            const card = node('a', 'mv-discovery__recent-card');
            card.href = new URL(`products/${encodeURIComponent(handle)}`, root).href;
            const photo = node('div', 'mv-discovery__recent-image');
            const picture = node('img');
            picture.src = image;
            picture.alt = product.title;
            picture.loading = 'lazy';
            picture.width = 420;
            picture.height = 420;
            photo.append(picture);
            const prefix = product.price_varies ? `${this.dataset.from} ` : '';
            const price = `${prefix}${formatter.format(product.price / 100)}`;
            const preorder = Array.isArray(product.tags) && product.tags.includes('mervaux-preorder');
            const state = !product.available ? this.dataset.soldOut : preorder ? this.dataset.preorder : this.dataset.available;
            card.append(photo, node('h3', 'mv-discovery__recent-title', product.title), node('span', 'mv-discovery__recent-price', price), node('span', 'mv-discovery__recent-state', state));
            return card;
          } catch { return null; }
        }));
        if (request.signal.aborted || !allowed() || !this.isConnected) return;
        const cards = results.filter(Boolean);
        list.replaceChildren(...cards);
        this.hidden = cards.length === 0;
      } catch {}
    }
  }
  window.mervauxRecentlyViewed = { refresh };
  if (!customElements.get('mervaux-recently-viewed')) customElements.define('mervaux-recently-viewed', RecentlyViewed);
  document.addEventListener('visitorConsentCollected', refresh);
  document.addEventListener('shopify:section:load', loadPrivacy);
  window.addEventListener('storage', (event) => { if (event.key === key) renderViews(); });
  window.addEventListener('pageshow', loadPrivacy);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadPrivacy, { once: true });
  else loadPrivacy();
  window.addEventListener('load', loadPrivacy, { once: true });
})();
